import { useState } from 'react';

// Types
export type TeamColor = 'BLUE' | 'RED' | 'GREEN' | 'YELLOW';

export interface CharacterSheet {
  id: string;
  name: string;
  description: string;
  imageUrl?: string;
  bonuses: Array<{ attribute: string; value: number }>;
  maluses: Array<{ attribute: string; value: number }>;
  moves: Array<{ name: string; description: string }>;
  notes?: string;
}

export interface SheetAssignment {
  id: string;
  sheetId: string;
  teamColor: string;
  isPrimary: boolean;
  sheet: CharacterSheet;
}

export interface TeamConfigProps {
  teamColor: TeamColor;
  allSheets: CharacterSheet[];
  assignments: SheetAssignment[];
  onAssign: (teamColor: TeamColor, sheetId: string, isPrimary: boolean) => Promise<boolean>;
  onUnassign: (assignmentId: string) => void;
  onTogglePrimary: (assignmentId: string, currentIsPrimary: boolean) => void;
  onEditSheet?: (sheet: CharacterSheet) => void;
}

const TEAM_COLOR_CLASSES: Record<TeamColor, { bg: string; text: string; border: string }> = {
  BLUE: { bg: 'bg-blue-900', text: 'text-blue-300', border: 'border-blue-600' },
  RED: { bg: 'bg-red-900', text: 'text-red-300', border: 'border-red-600' },
  GREEN: { bg: 'bg-green-900', text: 'text-green-300', border: 'border-green-600' },
  YELLOW: { bg: 'bg-yellow-900', text: 'text-yellow-300', border: 'border-yellow-600' },
};

export function TeamConfig({
  teamColor,
  allSheets,
  assignments,
  onAssign,
  onUnassign,
  onTogglePrimary,
  onEditSheet,
}: TeamConfigProps) {
  const [showAddSheet, setShowAddSheet] = useState(false);
  const [selectedSheetId, setSelectedSheetId] = useState('');
  const [isPrimary, setIsPrimary] = useState(true);
  const [isAdding, setIsAdding] = useState(false);

  const colors = TEAM_COLOR_CLASSES[teamColor];
  const assignedSheetIds = assignments.map((a) => a.sheetId);
  const availableSheets = allSheets.filter((sheet) => !assignedSheetIds.includes(sheet.id));

  const handleAdd = async () => {
    if (!selectedSheetId || isAdding) return;

    setIsAdding(true);
    const success = await onAssign(teamColor, selectedSheetId, isPrimary);
    setIsAdding(false);

    if (success) {
      setSelectedSheetId('');
      setIsPrimary(true);
      setShowAddSheet(false);
    }
  };

  return (
    <div className={`${colors.bg} bg-opacity-20 rounded-xl p-6 border ${colors.border}`}>
      <div className="flex justify-between items-center mb-4">
        <h3 className={`text-xl font-bold ${colors.text}`}>Team {teamColor}</h3>
        <button
          onClick={() => setShowAddSheet(!showAddSheet)}
          className={`${colors.text} hover:opacity-80 text-sm font-semibold`}
        >
          {showAddSheet ? '✕ Cancel' : '+ Add Sheet'}
        </button>
      </div>

      {/* Add Sheet Form */}
      {showAddSheet && (
        <div className="mb-4 p-4 bg-gray-800 rounded-lg border border-gray-700">
          <select
            value={selectedSheetId}
            onChange={(e) => setSelectedSheetId(e.target.value)}
            className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white mb-3"
          >
            <option value="">Select a character sheet...</option>
            {availableSheets.map((sheet) => (
              <option key={sheet.id} value={sheet.id}>
                {sheet.name}
              </option>
            ))}
          </select>

          <label className="flex items-center gap-2 text-white mb-3">
            <input
              type="checkbox"
              checked={isPrimary}
              onChange={(e) => setIsPrimary(e.target.checked)}
              className="w-4 h-4"
            />
            <span className="text-sm">Primary (shown in voting)</span>
          </label>

          <button
            onClick={handleAdd}
            disabled={!selectedSheetId || isAdding}
            className="w-full bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white py-2 rounded font-semibold"
          >
            {isAdding ? 'Adding...' : 'Add Sheet'}
          </button>
        </div>
      )}

      {/* Assigned Sheets */}
      <div className="space-y-3">
        {assignments.length === 0 ? (
          <p className="text-gray-400 text-sm italic">No sheets assigned yet</p>
        ) : (
          assignments.map((assignment) => (
            <div
              key={assignment.id}
              className="bg-gray-800 rounded-lg p-4 border border-gray-700"
            >
              <div className="flex justify-between items-start mb-2">
                <div className="flex-1">
                  <h4 className="text-white font-semibold">{assignment.sheet.name}</h4>
                  <p className="text-gray-400 text-xs line-clamp-2 mt-1">
                    {assignment.sheet.description}
                  </p>
                </div>
                <button
                  onClick={() => onUnassign(assignment.id)}
                  className="text-red-400 hover:text-red-300 text-sm ml-2"
                >
                  ✕
                </button>
              </div>

              <div className="flex items-center gap-2 mt-3">
                <button
                  onClick={() => onTogglePrimary(assignment.id, assignment.isPrimary)}
                  className={`px-3 py-1 rounded text-xs font-semibold ${
                    assignment.isPrimary
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-700 text-gray-400'
                  }`}
                >
                  {assignment.isPrimary ? '⭐ Primary' : '🎲 Secondary'}
                </button>
                {onEditSheet && (
                  <button
                    onClick={() => onEditSheet(assignment.sheet)}
                    className="px-3 py-1 rounded text-xs font-semibold bg-blue-600 hover:bg-blue-500 text-white"
                  >
                    ✏️ Edit
                  </button>
                )}
                <span className="text-gray-500 text-xs">
                  {assignment.isPrimary ? '(Shown in voting)' : '(Random option only)'}
                </span>
              </div>

              {/* Preview bonuses/maluses */}
              <div className="mt-3 grid grid-cols-2 gap-2">
                {assignment.sheet.bonuses.length > 0 && (
                  <div>
                    <p className="text-xs text-green-400 font-semibold mb-1">BONUSES</p>
                    <div className="flex flex-wrap gap-1">
                      {assignment.sheet.bonuses.slice(0, 3).map((bonus, idx) => (
                        <span
                          key={idx}
                          className="bg-green-900 bg-opacity-30 text-green-300 text-xs px-2 py-0.5 rounded"
                        >
                          {bonus.attribute} +{bonus.value}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {assignment.sheet.maluses.length > 0 && (
                  <div>
                    <p className="text-xs text-red-400 font-semibold mb-1">MALUSES</p>
                    <div className="flex flex-wrap gap-1">
                      {assignment.sheet.maluses.slice(0, 3).map((malus, idx) => (
                        <span
                          key={idx}
                          className="bg-red-900 bg-opacity-30 text-red-300 text-xs px-2 py-0.5 rounded"
                        >
                          {malus.attribute} {malus.value}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
